/**
 * کاربرانِ مسدودشده — قرینه‌ی `apps/mobile/app/blocked.tsx`.
 *
 * WHOSUP-230، مایگریشن 0146.
 *
 * فهرست از RPCِ `list_blocked_users` می‌آید و نه از یک select روی `blocks`:
 * سیاستِ `p_sel` پروفایلِ مسدودشده را از دیدِ خودِ مسدودکننده هم پنهان
 * می‌کند (و باید بکند، وگرنه «پیدا نشد» فقط یک‌طرفه بود)، پس یک joinِ ساده
 * ردیف‌های بی‌نام و بی‌عکس می‌داد. توضیحِ کاملش بالای `blockRepo`.
 *
 * رفعِ مسدودیت عمداً بی‌تأییدیه است: برخلافِ خودِ مسدود کردن، کارِ
 * برگشت‌پذیری است و دوباره زدنش همان حالت را برمی‌گرداند.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { blockRepo } from '@/core/data/blockRepo';
import { useSession } from '@/core/session';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { Avatar, Card, Note } from '@/ui/kit';

export function BlockedUsersPage() {
  useLocale();
  const { profile } = useSession();
  const qc = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);

  const blocked = useQuery({
    queryKey: ['blockedUsers'],
    queryFn: () => blockRepo.listBlocked(),
    enabled: !!profile,
  });

  const unblock = useMutation({
    mutationFn: (id: string) => blockRepo.unblock(id),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['blockedUsers'] });
      // دوستی برنمی‌گردد، ولی پروفایلش دوباره پیدا می‌شود — پس فهرست‌هایی
      // که «پیدا نشد» می‌گرفتند باید از نو بپرسند.
      qc.invalidateQueries({ queryKey: ['friends'] });
    },
    onError: (e) => { reportError('web.block.unblock', e); setError(t('errorRetry')); },
  });

  return (
    <div className="feed">
      <h1 className="feed-title">{t('blockedUsers')}</h1>
      <p className="dim">{t('blockedUsersSub')}</p>

      {blocked.isLoading ? <p className="dim">…</p> : null}
      {error ? <p className="error">{error}</p> : null}

      {blocked.data?.length === 0 ? (
        <div data-testid="blocked-empty"><Note>{t('blockedNone')}</Note></div>
      ) : null}

      {(blocked.data ?? []).map((b) => (
        <Card key={b.id} className="friend-row">
          <Avatar url={b.avatarUrl} name={b.displayName} size={30} />
          <span className="person-main">
            <span className="person-name">{b.displayName}</span>
            {b.username ? <span className="person-sub">{`@${b.username}`}</span> : null}
          </span>
          <button type="button" className="btn" data-testid={`unblock-${b.id}`}
            disabled={unblock.isPending}
            onClick={() => unblock.mutate(b.id)}>
            {unblock.isPending ? '…' : t('unblockAction')}
          </button>
        </Card>
      ))}

      {/* همان حرفی که مودالِ تأیید می‌زند، این‌جا هم لازم است: کسی که برای
          رفعِ مسدودیت آمده باید بداند دوستیِ قطع‌شده خودکار برنمی‌گردد. */}
      {blocked.data?.length ? <Note>{t('blockKeepsShared')}</Note> : null}
    </div>
  );
}
